import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import { api } from "../api/client";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { formatIDR } from "../components/Shared";
import { useBranding, logoUrl, pickText } from "../contexts/BrandingContext";
import { Flame, ArrowRight, UserPlus, CreditCard, ShieldCheck, GraduationCap, CalendarDays, Users, Check } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1641337221253-fdc7237f6b61?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const COACH_IMG = "https://images.unsplash.com/photo-1554344728-77cf90d9ed26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const STEP_ICONS = [UserPlus, CreditCard, ShieldCheck, GraduationCap];

export default function Landing() {
  const { t, lang } = useLang();
  const { branding } = useBranding();
  const [data, setData] = useState(null);
  const [gallery, setGallery] = useState([]);
  const accent = branding?.accent_color || "#FF3B30";
  const appName = branding?.app_name || t("common.appName");
  const tagline = pickText(branding?.tagline, lang) || t("common.appTagline");

  useEffect(() => {
    api.get("/public/landing").then(({ data }) => setData(data.data)).catch(() => {});
    api.get("/public/transformations").then(({ data }) => setGallery(data.data.items || [])).catch(() => {});
  }, []);

  const brandSteps = (branding?.steps || []).filter((s) => pickText(s.title, lang));
  const steps = (brandSteps.length ? brandSteps.map((s, i) => ({
    icon: STEP_ICONS[i % 4],
    title: pickText(s.title, lang),
    body: pickText(s.body, lang) || "",
  })) : [0, 1, 2, 3].map((i) => ({
    icon: STEP_ICONS[i],
    title: t(`landing.step${i + 1}Title`),
    body: t(`landing.step${i + 1}Body`),
  })));
  const brandFaq = (branding?.faq || []).filter((f) => pickText(f.q, lang));
  const faqs = brandFaq.length
    ? brandFaq.map((f) => ({ q: pickText(f.q, lang), a: pickText(f.a, lang) || "" }))
    : [1, 2, 3, 4].map((i) => ({ q: t(`landing.faq${i}Q`), a: t(`landing.faq${i}A`) }));
  const socials = Object.entries(branding?.socials || {}).filter(([, v]) => v);

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#090A0F] text-slate-200">
      <header className="border-b border-white/5 bg-[#090A0F]/85 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
            {branding?.logo_file_id ? (
              <img src={logoUrl(branding.logo_file_id)} alt="logo" className="h-9 w-9 rounded-lg object-contain bg-white/5 p-0.5" />
            ) : (
              <span className="h-9 w-9 rounded-lg flex items-center justify-center shadow-lg shadow-red-900/30" style={{ background: `linear-gradient(135deg, ${accent}, #E02E24)` }}>
                <Flame className="h-5 w-5 text-white" strokeWidth={2.5} />
              </span>
            )}
            <span className="font-[Outfit] font-extrabold text-white tracking-tight text-lg">{appName}</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/login"><Button data-testid="landing-login-btn" variant="ghost" className="h-9 text-slate-200 hover:bg-white/5 hover:text-white">{t("auth.loginSubmit")}</Button></Link>
            <Link to="/register" className="hidden sm:block">
              <Button data-testid="landing-register-btn" className="h-9 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">{t("auth.registerLink")}</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#090A0F] via-[#090A0F]/90 to-transparent" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-6 py-20 sm:py-28">
          <span className="text-xs uppercase tracking-[0.25em] text-[#10B981] font-semibold">{pickText(branding?.hero_eyebrow, lang) || t("landing.eyebrow")}</span>
          <h1 data-testid="landing-hero-title" className="mt-5 max-w-2xl font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
            {pickText(branding?.hero_title, lang) || t("landing.heroTitle")}
          </h1>
          <p className="mt-6 max-w-xl text-base text-slate-300 leading-relaxed">{pickText(branding?.hero_body, lang) || t("landing.heroBody")}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/register">
              <Button data-testid="landing-hero-cta" className="h-12 px-7 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold text-base shadow-lg shadow-red-900/30">
                {t("auth.registerLink")} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="h-12 px-7 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white font-semibold">
                {t("auth.loginSubmit")}
              </Button>
            </Link>
          </div>
          {data && (
            <div className="mt-14 flex flex-wrap gap-8">
              {[
                { v: data.active_members, l: t("admin.activeParticipants"), icon: Users },
                { v: data.coach_count, l: "Coach", icon: GraduationCap },
                { v: data.programs.length, l: t("nav.programs"), icon: CalendarDays },
              ].map((s) => (
                <div key={s.l} className="flex items-center gap-3">
                  <s.icon className="h-5 w-5 text-[#10B981]" />
                  <div>
                    <div className="font-[Outfit] text-2xl font-extrabold text-white leading-none">{s.v}</div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{s.l}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-5 sm:px-6 py-20">
        <h2 className="font-[Outfit] text-base md:text-lg font-bold uppercase tracking-[0.2em] text-[#10B981]">{t("landing.howTitle")}</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={s.title} data-testid={`landing-step-${i + 1}`} className="rounded-2xl border border-white/5 bg-white/[0.02] p-6 hover:border-white/10 hover:bg-white/[0.04] transition">
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-[#FF3B30]/10 text-[#FF3B30] flex items-center justify-center"><s.icon className="h-5 w-5" /></span>
                <span className="font-[Outfit] text-3xl font-extrabold text-white/10">0{i + 1}</span>
              </div>
              <div className="mt-4 font-semibold text-white">{s.title}</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fee */}
      <section className="border-y border-white/5 bg-gradient-to-br from-[#0E1018] to-[#161923]">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-[Outfit] text-base md:text-lg font-bold uppercase tracking-[0.2em] text-[#10B981]">{t("landing.feeTitle")}</h2>
            <div className="mt-6 flex items-end gap-3">
              <span data-testid="landing-base-fee" className="font-[Outfit] text-5xl font-extrabold text-white">{formatIDR(data?.base_fee ?? 50000)}</span>
              <span className="text-2xl text-[#F59E0B] font-semibold">+ {t("payments.uniqueCode")}</span>
            </div>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-lg">{t("landing.feeBody")}</p>
            <ul className="mt-6 space-y-2">
              {[t("landing.feePoint1"), t("landing.feePoint2"), t("landing.feePoint3")].map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-[#10B981] mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0B0C13] p-6 font-mono text-sm space-y-3">
            <div className="flex justify-between text-slate-400"><span>{t("payments.baseFee")}</span><span>{formatIDR(50000)}</span></div>
            <div className="flex justify-between text-slate-400"><span>{t("payments.uniqueCode")}</span><span>+ 018</span></div>
            <div className="border-t border-white/10 pt-3 flex justify-between text-white font-bold"><span>{t("payments.totalToPay")}</span><span className="text-[#F59E0B]">{formatIDR(50018)}</span></div>
            <p className="text-[11px] text-slate-500 font-sans leading-relaxed pt-2">{t("payments.exactAmountHint")}</p>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="mx-auto max-w-6xl px-5 sm:px-6 py-20">
        <h2 className="font-[Outfit] text-base md:text-lg font-bold uppercase tracking-[0.2em] text-[#10B981]">{t("landing.programsTitle")}</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {(data?.programs || []).map((p) => (
            <div key={p.program_id} data-testid={`landing-program-${p.program_id}`} className="rounded-2xl border border-white/5 bg-[#161923] p-6">
              <div className="font-[Outfit] text-xl font-bold text-white">{p.name}</div>
              {p.description && <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.description}</p>}
              <div data-testid={`landing-program-duration-${p.program_id}`} className="mt-3 text-xs uppercase tracking-widest text-slate-500">{p.duration_days} {t("programs.days")}</div>
              <div className="mt-5 space-y-2">
                {p.batches.length === 0 ? (
                  <p className="text-xs text-slate-500">{t("landing.noOpenBatch")}</p>
                ) : p.batches.map((b) => (
                  <div key={b.batch_id} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-xs">
                    <span className="text-slate-300">{b.name}</span>
                    <span className="text-[#10B981] font-semibold">{b.seats_left} {t("landing.seatsLeft")}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {data?.programs?.length === 0 && <p className="text-sm text-slate-500">{t("landing.noOpenBatch")}</p>}
        </div>
      </section>

      {/* Transformation gallery */}
      {gallery.length > 0 && (
        <section data-testid="landing-gallery" className="border-y border-white/5 bg-[#0B0C13]">
          <div className="mx-auto max-w-6xl px-5 sm:px-6 py-20">
            <h2 className="font-[Outfit] text-base md:text-lg font-bold uppercase tracking-[0.2em] text-[#10B981]">{t("landing.galleryTitle")}</h2>
            <p className="mt-4 max-w-xl text-sm text-slate-400 leading-relaxed">{t("landing.galleryBody")}</p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {gallery.map((g, i) => (
                <div key={`${g.display_name}-${i}`} data-testid={`landing-gallery-${i + 1}`}
                  className="rounded-2xl border border-white/5 bg-[#161923] overflow-hidden hover:border-[#10B981]/30 transition">
                  <div className="grid grid-cols-2">
                    {["before", "after"].map((slot) => (
                      <div key={slot} className="relative">
                        <img src={logoUrl(slot === "before" ? g.photo_before_file_id : g.photo_after_file_id)}
                          alt={slot} className="h-44 w-full object-cover" />
                        <span className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white">
                          {t(`recog.${slot}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="p-4">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold text-white">{g.display_name}</span>
                      <span className="font-[Outfit] text-xl font-extrabold text-emerald-300">-{g.weight_lost} kg</span>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      {g.weight_before} → {g.weight_after} kg
                      {g.fat_lost ? ` · body fat -${g.fat_lost}%` : ""}
                      {g.batch_name ? ` · ${g.batch_name}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[11px] text-slate-600">{t("landing.galleryConsentNote")}</p>
          </div>
        </section>
      )}

      {/* Coach */}
      <section className="border-y border-white/5">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <img src={COACH_IMG} alt="" className="rounded-2xl h-72 w-full object-cover border border-white/5" />
          <div>
            <h2 className="font-[Outfit] text-base md:text-lg font-bold uppercase tracking-[0.2em] text-[#10B981]">{t("landing.coachTitle")}</h2>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed">{t("landing.coachBody")}</p>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">{t("auth.coachInfoBody")}</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 sm:px-6 py-20">
        <h2 className="font-[Outfit] text-base md:text-lg font-bold uppercase tracking-[0.2em] text-[#10B981]">{t("landing.faqTitle")}</h2>
        <div className="mt-8 divide-y divide-white/5">
          {faqs.map((f, i) => (
            <details key={f.q} data-testid={`landing-faq-${i + 1}`} className="group py-5">
              <summary className="cursor-pointer text-white font-medium list-none flex items-center justify-between gap-4">
                {f.q}
                <span className="text-[#FF3B30] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-5 sm:px-6 pb-24">
        <div className="rounded-3xl border border-[#FF3B30]/20 bg-gradient-to-br from-[#1a0f10] to-[#161923] p-10 sm:p-14 text-center">
          <h2 className="font-[Outfit] text-3xl sm:text-4xl font-extrabold text-white tracking-tight">{t("landing.ctaTitle")}</h2>
          <p className="mt-4 text-sm text-slate-400 max-w-xl mx-auto leading-relaxed">{t("landing.ctaBody")}</p>
          <Link to="/register" className="inline-block mt-8">
            <Button data-testid="landing-footer-cta" className="h-12 px-8 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold text-base">
              {t("auth.registerLink")} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-slate-600 space-y-3">
        <div data-testid="landing-footer-contact" className="flex flex-wrap items-center justify-center gap-4 text-slate-400">
          {branding?.contact_whatsapp && (
            <a href={`https://wa.me/${branding.contact_whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="hover:text-white transition">WhatsApp {branding.contact_whatsapp}</a>
          )}
          {branding?.contact_email && <a href={`mailto:${branding.contact_email}`} className="hover:text-white transition">{branding.contact_email}</a>}
        </div>
        {socials.length > 0 && (
          <div data-testid="landing-footer-socials" className="flex flex-wrap items-center justify-center gap-4">
            {socials.map(([k, v]) => (
              <a key={k} href={v} target="_blank" rel="noreferrer" className="capitalize hover:text-white transition">{k}</a>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-center gap-4 text-slate-400">
          <Link to="/privacy" data-testid="landing-privacy-link" className="hover:text-white transition">{t("legal.privacy")}</Link>
          <Link to="/terms" data-testid="landing-terms-link" className="hover:text-white transition">{t("legal.terms")}</Link>
        </div>
        <div>© {new Date().getFullYear()} {appName} · {tagline}</div>
      </footer>
    </div>
  );
}
